/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * use_udc_form_fields - feed custom fields into a screen's FieldRenderer.
 *
 * Companion to useUdcGrid (list columns/facets). This turns the custom fields an
 * admin placed on a page's FORM into first-class FieldRenderer items, so they:
 *   - interleave with native fields (one shared FieldDef[] + layout), and
 *   - inherit the SAME personalization as native fields — hide / read-only /
 *     required / reorder / move-between-sections, per System/Role/User tier —
 *     because they flow through the same FieldRenderer + PersonalizedField path.
 *
 * The hook owns the custom values: it loads the row's ext_attributes lazily (only
 * when fields are placed and a record exists) and persists edits autonomously,
 * debounced — so a host modal needs no extra form-state or save wiring.
 *
 * Usage in a modal that already renders native fields via <FieldRenderer>:
 *   const udcForm = useUdcFormFields(entity, PAGE_CODE, { pkColumn, pk });
 *   useUldLayout(PAGE_CODE, [...NATIVE_CORE_LAYOUT, ...udcForm.coreLayout]);
 *   <FieldRenderer fields={[...NATIVE_FIELD_DEFS, ...udcForm.fieldDefs]} ...
 *     render={f => { ...native cases...;
 *       if (udcForm.isUdcKey(f.key)) return (
 *         <PersonalizedField componentKey={CK} fieldKey={f.key} label={f.label}
 *           readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
 *           {udcForm.renderControl(f.key)}
 *         </PersonalizedField>);
 *       return null; }} />
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createElement } from 'react';
import { apiDb } from '../lib/api_client';
import { useUdcFields } from './use_udc_fields';
import { UdcControl } from '../components/common/UdcCustomFields';
import { toUldCoreLayout } from '../components/common/FieldRenderer';
import type { FieldDef } from '../components/common/FieldRenderer';
import type { UldLayoutItem } from '../lib/uld_merge';

export interface UseUdcFormFields {
  fieldDefs: FieldDef[];
  coreLayout: UldLayoutItem[];
  isUdcKey: (key: string) => boolean;
  renderControl: (key: string, opts?: { disabled?: boolean }) => ReactNode;
  hasFields: boolean;
}

interface Opts {
  pkColumn: string;
  pk: number | string | null | undefined;
  workspaceCode?: string | null;
}

export function useUdcFormFields(entity: string | null | undefined, pageCode: string, opts: Opts): UseUdcFormFields {
  const { pkColumn, pk } = opts;
  const ws = opts.workspaceCode !== undefined ? (opts.workspaceCode ?? '') : undefined;
  const { fields } = useUdcFields(entity, { pageCode, workspaceCode: ws });
  const [ext, setExt] = useState<Record<string, unknown>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Load the row's shadow attributes once a record + placed fields exist.
  useEffect(() => {
    if (pk == null || fields.length === 0 || !entity) { setExt({}); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb.from(entity).select('ext_attributes').eq(pkColumn, pk).single();
      if (!cancelled) setExt(((data as { ext_attributes?: Record<string, unknown> } | null)?.ext_attributes) ?? {});
    })();
    return () => { cancelled = true; };
  }, [entity, pkColumn, pk, fields.length]);

  // Custom fields as FieldRenderer items. Full-width in the host's column grid
  // (col_span >= columns ⇒ col-span-full); section carries the placement section.
  const fieldDefs: FieldDef[] = useMemo(
    () => fields.map(f => ({
      key: f.key,
      type: 'custom' as const,
      label: f.label,
      required: f.required,
      col_span: 12,
      section: f.section_code ?? undefined,
    })),
    [fields],
  );
  const coreLayout = useMemo(() => toUldCoreLayout(fieldDefs), [fieldDefs]);

  const byKey = useMemo(() => {
    const m = new Map(fields.map(f => [f.key, f]));
    return m;
  }, [fields]);
  const isUdcKey = useCallback((key: string) => byKey.has(key), [byKey]);

  const save = useCallback((next: Record<string, unknown>) => {
    if (pk == null || !entity) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void apiDb.from(entity).update({ ext_attributes: next }).eq(pkColumn, pk);
    }, 600);
  }, [entity, pkColumn, pk]);

  const renderControl = useCallback((key: string, o?: { disabled?: boolean }): ReactNode => {
    const f = byKey.get(key);
    if (!f) return null;
    return createElement(UdcControl, {
      field: f,
      value: ext[key],
      disabled: o?.disabled,
      onChange: (v: unknown) => { const nextVal = { ...ext, [key]: v }; setExt(nextVal); save(nextVal); },
    });
  }, [byKey, ext, save]);

  return { fieldDefs, coreLayout, isUdcKey, renderControl, hasFields: fields.length > 0 };
}
